import { Url } from "@edgetier/types";
import { faSignOutAlt } from "@fortawesome/free-solid-svg-icons/faSignOutAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Axios from "axios";
import { Fragment, FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { authenticationOperations } from "redux/modules/authentication";
import { IQueryEmail } from "redux/modules/email/email.types";
import { loadingBlockerOperations } from "redux/modules/loading-blocker";
import { IApplicationState } from "redux/types";
import { Modal, ModalContainer } from "shared/modal";
import axios from "utilities/axios";

import { useFlagsUtilities } from "@edgetier/feature-flags";
import { Permission } from "@edgetier/types";
import usePermission from "hooks-for/permissions/use-permission";
import { IThunkDispatch } from "redux/types";
import "./sign-out.scss";
import { IProps } from "./sign-out.types";
import Warnings from "./warnings";

/**
 * Sign out button.
 */
export const SignOut: FunctionComponent<IProps> = () => {
    const flagsUtils = useFlagsUtilities();
    const cancelTokenSource = useRef(Axios.CancelToken.source());
    useEffect(() => cancelTokenSource.current.cancel, []);

    const dispatch = useDispatch<IThunkDispatch>();

    const chatCount = useSelector((state: IApplicationState) => Object.keys(state.chat.chats).length);

    const [deferredQueryCount, setDeferredQueryCount] = useState(0);

    const { hasPermission: handlesInteractions } = usePermission(Permission.HandleInteraction);

    /**
     * Request deferred queries for the user.
     * @returns List of deferred queries.
     */
    const countDeferredQueries = async (): Promise<number> => {
        try {
            dispatch(loadingBlockerOperations.showLoadingBlocker(true));
            const configuration = { cancelToken: cancelTokenSource.current.token };
            const response = await axios.get<IQueryEmail[]>(Url.EmailsDeferred, configuration);
            return response.data.length;
        } catch (serverError) {
            return 0;
        } finally {
            dispatch(loadingBlockerOperations.hideLoadingBlocker(true));
        }
    };

    /**
     * Agents need to be warned about any deferred queries and/or active chats when they sign out.
     * @param showModal Method to show warnings in a modal.
     */
    const generateWarnings = async (showModal: VoidFunction): Promise<void> => {
        if (handlesInteractions) {
            const deferredQueryCount = await countDeferredQueries();
            if (chatCount === 0 && deferredQueryCount === 0) {
                dispatch(authenticationOperations.signOut());
            } else {
                setDeferredQueryCount(deferredQueryCount);
                showModal();
            }
        } else {
            dispatch(authenticationOperations.signOut());
        }
    };

    /**
     * Sign the user out of the application.
     */
    const signOut = useCallback(
        (hideModal: VoidFunction): void => {
            if (process.env.NODE_ENV !== "development") {
                flagsUtils.logout();
            }
            hideModal();
            dispatch(authenticationOperations.signOut());
        },
        [dispatch, flagsUtils]
    );

    return (
        <ModalContainer>
            {({ hideModal, showModal }) => (
                <Fragment>
                    <Modal>
                        <h2>Warning</h2>
                        <Warnings
                            chatCount={chatCount}
                            deferredQueryCount={deferredQueryCount}
                            hideModal={hideModal}
                            signOut={signOut.bind(this, hideModal)}
                        />
                    </Modal>
                    <li
                        className="navigation-link navigation__sign-out"
                        onClick={generateWarnings.bind(this, showModal)}
                        role="button"
                    >
                        <div className="navigation-link__inner">
                            <FontAwesomeIcon icon={faSignOutAlt} />
                            Sign Out
                        </div>
                    </li>
                </Fragment>
            )}
        </ModalContainer>
    );
};

export default SignOut;
